import { Annotorious, ImageAnnotation, ImageAnnotator, useAnnotator } from "@annotorious/react"
import { DettaglioTavola, ImmagineAtlante, Tavola } from "../../types"
import "@annotorious/react/annotorious-react.css"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { annotationToCoords, dettaglioTavolaToAnnotation, hasCoords } from "../../utils/annotations"
import { Collapse } from "react-bootstrap"
import { Button, H5 } from "@inmagik/magik-ui"
import { useImmaginiAtlante, useUpdateImageDetailsCoords, useDropImageDetailCoords } from "../../hooks/immaginIAtlante"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import ModalDelete from "../../components/ModalDelete"

type AnnotoriousInnerProps = {
  currentImage: ImmagineAtlante
  detail: number | null
  _setDetail: (id: number | null) => void
  setCurrentImage: (img: ImmagineAtlante) => void
  refetchImmagini: () => void
}

function AnnotoriousInner({
  currentImage,
  detail,
  _setDetail,
  setCurrentImage,
  refetchImmagini,
}: AnnotoriousInnerProps) {
  const annotator = useAnnotator()
  const updateImageDetailsCoords = useUpdateImageDetailsCoords(currentImage?.id || 0)
  const mouseState = useRef<"up" | "down" | null>(null)
  const detailRef = useRef<number | null>(null)

  useEffect(() => {
    detailRef.current = detail
  }, [detail])

  const setDetail = useCallback(
    (id: number | null) => {
      _setDetail(id)
      // detailRef.current = id
      if (annotator && currentImage) {
        const imageDetail = currentImage.dettagli_data.find((d) => d.dettaglio === id)
        if (!imageDetail) {
          annotator.setSelected()
        }
      }
    },
    [annotator, currentImage]
  )

  const setDetailValue = useCallback(
    (annotation: any) => {
      const nextAnnotation = {
        ...annotation,
        properties: {
          ...annotation.properties,
          id: detailRef.current,
        },
      }
      annotator.updateAnnotation(nextAnnotation)
      const coords = annotationToCoords(nextAnnotation)
      if (coords) {
        const localChanges = {
          [detailRef.current as number]: {
            top: coords.top,
            left: coords.left,
            bottom: coords.bottom,
            right: coords.right,
          },
        }

        const payload = Object.keys(localChanges).map((key) => ({
          ...localChanges[Number(key)],
          dettaglio: Number(key),
        }))

        if (!payload || payload.length === 0) return
        if (mouseState.current !== "up") return
        updateImageDetailsCoords
          .mutateAsync(payload)
          .then((resp) => {
            setCurrentImage({
              ...currentImage!,
              dettagli_data: resp.dettagli_data,
            })
            refetchImmagini()
            mouseState.current = null
          })
          .catch((err) => {
            console.error(err)
          })
      }
    },
    [annotator, updateImageDetailsCoords, currentImage, refetchImmagini]
  )

  const updateDetailValue = useCallback(
    (updated: any, previous: any) => {
      const coords = annotationToCoords(updated)
      if (detailRef.current && coords) {
        const localChanges = {
          [detailRef.current as number]: {
            top: coords.top,
            left: coords.left,
            bottom: coords.bottom,
            right: coords.right,
          },
        }
        const payload = Object.keys(localChanges).map((key) => ({
          ...localChanges[Number(key)],
          dettaglio: Number(key),
        }))

        if (!payload || payload.length === 0) return
        if (mouseState.current !== "up") return
        updateImageDetailsCoords
          .mutateAsync(payload)
          .then((resp) => {
            setCurrentImage({
              ...currentImage!,
              dettagli_data: resp.dettagli_data,
            })
            refetchImmagini()
            mouseState.current = null
          })
          .catch((err) => {
            console.error(err)
          })
      }
    },
    [annotator, updateImageDetailsCoords, currentImage, refetchImmagini]
  )

  useEffect(() => {
    if (!annotator) return
    annotator.on("createAnnotation", setDetailValue)
    annotator.on("updateAnnotation", updateDetailValue)

    return () => {
      annotator.off("createAnnotation", setDetailValue)
      annotator.off("updateAnnotation", updateDetailValue)
    }
  }, [annotator, setDetailValue, updateDetailValue])

  useEffect(() => {
    if (!annotator || !currentImage) return

    const nextAnnotations: ImageAnnotation[] = []

    currentImage.dettagli_data.forEach((dettaglio) => {
      const annotation = dettaglioTavolaToAnnotation(dettaglio, dettaglio.dettaglio)
      if (annotation) {
        nextAnnotations.push(annotation)
      }
    })
    // Set annotations
    // const currentAnnotations = annotator.getAnnotations() as ImageAnnotation[]
    // const idsToKeep: number[] = []

    annotator.clearAnnotations()

    nextAnnotations.forEach((annotation) => {
      annotator.addAnnotation(annotation)
      // idsToKeep.push(annotation.properties?.id as number)
      // const exists = currentAnnotations.find((a) => a.properties?.id === annotation.properties?.id)
      // if (!exists) {
      //   annotator.addAnnotation(annotation)
      // }
    })

    // // Remove annotations that are no longer present
    // currentAnnotations.forEach((annotation) => {
    //   if (!idsToKeep.includes(annotation.properties?.id as number)) {
    //     annotator.removeAnnotation(annotation)
    //   }
    // })
  }, [annotator, currentImage])

  useEffect(() => {
    if (!annotator) return
    annotator.setStyle((annotation, state) => {
      // If the annotation has an 'important' label, make it red
      const color = annotation.properties?.id.toString() === detail?.toString() ? "#ff0000" : "#ffffff"

      return {
        fill: color,
        stroke: "#ff0000",
        strokeOpacity: 1,
      }
    })
  }, [annotator, detail])

  useEffect(() => {
    if (!annotator) return

    function onSelectAnnotation(annotations: unknown[]) {
      if (!Array.isArray(annotations) || annotations.length === 0) {
        // setDetail(null)
        return
      }
      const annotation = annotations[0] as ImageAnnotation
      const id = Number(annotation.properties?.id)
      if (Number.isInteger(id)) {
        setDetail(id)
      }
    }

    annotator.on("selectionChanged", onSelectAnnotation)

    return () => {
      annotator.off("selectionChanged", onSelectAnnotation)
    }
  }, [annotator, detail])

  const currentDetailItem = useMemo(() => {
    if (!currentImage) return null
    return currentImage.dettagli_data.find((d) => d.dettaglio === detail) || null
  }, [detail, currentImage])

  const drawEnabled = useMemo(() => {
    if (!currentDetailItem) return true
    return detail !== null && !hasCoords(currentDetailItem)
  }, [currentDetailItem])

  return (
    <div
      className="w-100 h-100"
      onMouseDown={() => {
        mouseState.current = "down"
      }}
      onMouseUp={() => {
        mouseState.current = "up"
      }}
    >
      <ImageAnnotator
        drawingEnabled={drawEnabled}
        autoSave={true}
        tool={"rectangle"}
        drawingMode={"drag"}
        containerClassName="h-100 w-100 bg-dark position-relative"
      >
        <div className="h-100 w-100 position-absolute top-0 start-0">
          <img src={currentImage.immagine} className="h-100 w-100" style={{ objectFit: "contain" }} />
        </div>
      </ImageAnnotator>
    </div>
  )
}

function AnnotoriousWrapper(props: AnnotoriousInnerProps) {
  return (
    <Annotorious>
      <AnnotoriousInner {...props} />
    </Annotorious>
  )
}

type TavolaMappaProps = {
  tavola: Tavola
  dettagliTavola?: DettaglioTavola[]
}

export default function TavolaMappaDettagliImmagini({ tavola, dettagliTavola }: TavolaMappaProps) {
  const { data: immaginiAtlante, refetch: refetchImmagini } = useImmaginiAtlante({
    tavola: tavola?.id.toString() || "",
  })
  const [currentImage, setCurrentImage] = useState<ImmagineAtlante | null>(null)

  const dropImageDetailCoords = useDropImageDetailCoords(currentImage?.id || 0)
  const [modalDropDetailCoordsState, modalDropDetailCoordsActions] = useModalTrigger()

  const [detail, _setDetail] = useState<number | null>(null)

  return (
    <div className="w-100 flex-grow-1 px-3 position-relative">
      <div className="position-absolute top-0 start-0 bottom-0 end-0">
        <div className="row" style={{ height: "100%" }}>
          <div className="col-2 h-100 overflow-auto">
            <div className="d-flex flex-column align-items-start mb-2 bg-primary p-2 rounded">
              <div className="bg-primary text-white">Immagini atlante</div>
            </div>
            <div className="px-2">
              <p className="smaller-text">Seleziona un'immagine per definire i dettagli</p>
            </div>
            {immaginiAtlante?.results.map((immagine) => (
              <ImmagineItem
                currentImage={currentImage}
                setCurrentImage={setCurrentImage}
                immagine={immagine}
                key={immagine.id}
              />
            ))}
          </div>
          <div className="col-6 h-100 bg-dark-subtle rounded position-relative">
            {currentImage && (
              <AnnotoriousWrapper
                key={currentImage.id}
                currentImage={currentImage}
                detail={detail}
                _setDetail={_setDetail}
                setCurrentImage={setCurrentImage}
                refetchImmagini={refetchImmagini}
              />
            )}
            {!currentImage && (
              <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                <div className="w-50 text-center text-muted">Seleziona un'immagine per modificarne i dettagli</div>
              </div>
            )}
          </div>

          <div className="col-4 h-100 overflow-auto">
            <div className="d-flex flex-column align-items-start mb-2 bg-primary p-2 rounded">
              <div className="bg-primary text-white">Dettagli Tavola</div>
            </div>
            <div className="px-2">
              <p className="smaller-text">Seleziona un dettaglio per disegnarlo o modificarlo</p>
            </div>

            {(dettagliTavola || []).map((dettaglio) => (
              <DettaglioItem
                removeDetail={(id) => {
                  modalDropDetailCoordsActions.open(id)
                }}
                key={dettaglio.id}
                dettaglio={dettaglio}
                detail={detail}
                setDetail={_setDetail}
                currentImage={currentImage}
              />
            ))}
          </div>
        </div>
      </div>

      {modalDropDetailCoordsState.isOpen && (
        <ModalDelete
          isOpen={modalDropDetailCoordsState.isOpen}
          onClose={modalDropDetailCoordsActions.close}
          onDelete={() => {
            return dropImageDetailCoords.mutateAsync({ dettaglio: modalDropDetailCoordsState.value }).then((resp) => {
              modalDropDetailCoordsActions.close()
              if (currentImage) {
                setCurrentImage({
                  ...currentImage,
                  dettagli_data: resp.dettagli_data,
                })
              }
              refetchImmagini()
            })
          }}
          message="Sei sicuro di voler eliminare la posizione del dettaglio"
          title="Elimina posizione dettaglio"
        />
      )}
    </div>
  )
}

function DettaglioItem({
  dettaglio,
  detail,
  setDetail,
  currentImage,
  removeDetail,
}: {
  dettaglio: DettaglioTavola
  detail: null | number
  setDetail: (id: number | null) => void
  currentImage: ImmagineAtlante | null
  removeDetail: (id: number) => void
}) {
  const detailItem = useMemo(() => {
    if (!currentImage) return null
    return currentImage.dettagli_data.find((d) => d.dettaglio === dettaglio.id) || null
  }, [currentImage, dettaglio])

  const withCoords = useMemo(() => {
    if (!detailItem) return false
    return hasCoords(detailItem.dettaglio_data || dettaglio)
  }, [detailItem, dettaglio])

  return (
    <div
      className={`card mb-2 ${detail === dettaglio.id ? "bg-primary-subtle" : ""}`}
      key={dettaglio.id}
      onClick={() => setDetail(dettaglio.id)}
      style={{ cursor: "pointer" }}
    >
      <div className="card-body">
        <div className="d-flex flex-row align-items-center justify-content-between">
          <h6 className="card-title flex-grow-1 d-flex align-items-center gap-1">
            <div className={`d-flex flex-row align-items-center ${withCoords ? "text-success" : "text-danger"}`}>
              <i
                className="material-symbols-rounded"
                style={{
                  fontSize: 16,
                  fontVariationSettings: '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48',
                }}
              >
                circle
              </i>
            </div>
            {dettaglio.titolo}
          </h6>
          <div
            className="d-flex flex-row align-items-center gap-1"
            // style={{ opacity: detail === dettaglio.id ? 1 : 0 }}
          >
            {detail === dettaglio.id && withCoords && (
              <Button leadingIcon="close" variant="dark" onClick={() => removeDetail(dettaglio.id)} />
            )}
          </div>
        </div>
        <Collapse in={detail === dettaglio.id}>
          <div>
            <div dangerouslySetInnerHTML={{ __html: dettaglio.descrizione }}></div>
          </div>
        </Collapse>
      </div>
    </div>
  )
}

function ImmagineItem({
  immagine,
  setCurrentImage,
  currentImage,
}: {
  immagine: ImmagineAtlante
  setCurrentImage: (img: ImmagineAtlante) => void
  currentImage: ImmagineAtlante | null
}) {
  return (
    <div className="card mb-2" onClick={() => setCurrentImage(immagine)}>
      <div className={`card-body ${immagine.id === currentImage?.id ? "bg-primary-subtle" : ""}`}>
        <div className="d-flex flex-row align-items-center gap-3">
          <div className="w-25 flex-shrink-0 bg-dark" style={{ height: 100 }}>
            <img
              src={immagine.immagine}
              alt={immagine.titolo}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          <h6 className="card-title">{immagine.titolo}</h6>
        </div>
      </div>
    </div>
  )
}
